import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, ArrowLeft, Save, Edit2, Trash2 } from 'lucide-react';
import { NavigationMenu } from '../components/NavigationMenu';
import { Button } from '../components/Button';
import KanbanBoardStandalone, { BoardState } from '../components/KanbanBoardStandalone';
import { EditableTitle } from '../components/EditableTitle';
import { LoadingAnimation } from '../components/LoadingAnimation';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { knowledgeApi, KnowledgeItem } from '../services/api';
import { useCustomNavigate } from '../hooks/useCustomNavigate';
import { PageHeader } from '../components/PageHeader';

interface KanbanBoard {
  id: string;
  title: string;
  columns: Array<{
    id: string;
    title: string;
    cards: Array<{
      id: string;
      title: string;
      description?: string;
      priority?: 'low' | 'medium' | 'high' | 'critical';
      deadline?: string;
      tags?: string[];
    }>;
  }>;
}

interface KanbanDocumentData {
  boards: KanbanBoard[];
}

export default function KanbanDocument() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useCustomNavigate();
  
  const [document, setDocument] = useState<KnowledgeItem | null>(null);
  const [kanbanData, setKanbanData] = useState<KanbanDocumentData>({ boards: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useDocumentTitle(document?.name || 'Канбан доска');

  // Загрузка документа
  useEffect(() => {
    if (itemId) {
      loadDocument();
    }
  }, [itemId]);

  const loadDocument = async () => {
    if (!itemId) return;
    
    try {
      setIsLoading(true);
      const item = await knowledgeApi.getFile(itemId);
      
      if (item.fileType !== 'kanban') {
        navigate('/knowledge');
        return;
      }

      setDocument(item);
      
      // Парсим данные канбан
      if (item.content) {
        try {
          const parsedData = JSON.parse(item.content);
          setKanbanData(parsedData);
        } catch (error) {
          console.error('Ошибка парсинга данных канбан:', error);
          // Создаем дефолтную доску если данные повреждены
          setKanbanData({
            boards: [{
              id: 'default-board',
              title: 'Главная доска',
              columns: [
                { id: 'col-1', title: 'К выполнению', cards: [] },
                { id: 'col-2', title: 'В процессе', cards: [] },
                { id: 'col-3', title: 'Выполнено', cards: [] }
              ]
            }]
          });
        }
      } else {
        // Создаем дефолтную доску для нового документа
        setKanbanData({
          boards: [{
            id: 'default-board',
            title: 'Главная доска',
            columns: [
              { id: 'col-1', title: 'К выполнению', cards: [] },
              { id: 'col-2', title: 'В процессе', cards: [] },
              { id: 'col-3', title: 'Выполнено', cards: [] }
            ]
          }]
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки документа:', error);
      navigate('/knowledge');
    } finally {
      setIsLoading(false);
    }
  };

  const saveDocument = async () => {
    if (!document || !itemId) return;

    try {
      setIsSaving(true);
      const updatedDocument: KnowledgeItem = {
        ...document,
        content: JSON.stringify(kanbanData)
      };

      await knowledgeApi.updateItem(itemId, updatedDocument);
      setDocument(updatedDocument);
      setHasChanges(false);
    } catch (error) {
      console.error('Ошибка сохранения:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTitleUpdate = async (newTitle: string) => {
    if (!document || !itemId) return;

    try {
      const updatedDocument: KnowledgeItem = {
        ...document,
        name: newTitle
      };

      await knowledgeApi.updateItem(itemId, updatedDocument);
      setDocument(updatedDocument);
    } catch (error) {
      console.error('Ошибка обновления названия:', error);
    }
  };

  const addNewBoard = () => {
    const newBoard: KanbanBoard = {
      id: `board-${Date.now()}`,
      title: `Доска ${kanbanData.boards.length + 1}`,
      columns: [
        { id: `col-${Date.now()}-1`, title: 'К выполнению', cards: [] },
        { id: `col-${Date.now()}-2`, title: 'В процессе', cards: [] },
        { id: `col-${Date.now()}-3`, title: 'Выполнено', cards: [] }
      ]
    };

    setKanbanData(prev => ({
      boards: [...prev.boards, newBoard]
    }));
    setHasChanges(true);
  };

  const updateBoard = (boardId: string, updatedBoard: Partial<KanbanBoard>) => {
    setKanbanData(prev => ({
      boards: prev.boards.map(board =>
        board.id === boardId ? { ...board, ...updatedBoard } : board
      )
    }));
    setHasChanges(true);
  };

  const deleteBoard = (boardId: string) => {
    if (kanbanData.boards.length <= 1) {
      return;
    }

    setKanbanData(prev => ({
      boards: prev.boards.filter(board => board.id !== boardId)
    }));
    setHasChanges(true);
  };

  // Преобразование данных из нашего формата в BoardState
  const convertToBoardState = (board: KanbanBoard): BoardState => {
    const cards: { [key: string]: any } = {};
    const columns = board.columns.map(column => {
      const cardIds = column.cards.map(card => {
        cards[card.id] = {
          id: card.id,
          title: card.title,
          description: card.description,
          priority: card.priority || 'medium',
          deadline: card.deadline,
          tags: card.tags
        };
        return card.id;
      });

      return {
        id: column.id,
        title: column.title,
        cardIds
      };
    });

    return {
      columns,
      cards,
      columnOrder: columns.map(col => col.id),
      boardTitle: board.title
    };
  };

  const handleBoardUpdate = (boardId: string, newState: BoardState) => {
    // Преобразуем структуру данных из BoardState в нашу структуру
    const convertedColumns = newState.columns.map((column: any) => ({
      id: column.id,
      title: column.title,
      cards: column.cardIds.map((cardId: string) => {
        const card = newState.cards[cardId];
        return {
          id: card.id,
          title: card.title,
          description: card.description,
          priority: card.priority,
          deadline: card.deadline,
          tags: card.tags
        };
      })
    }));
    
    updateBoard(boardId, { columns: convertedColumns, title: newState.boardTitle });
  };

  if (isLoading) {
    return <LoadingAnimation withNavigation size="large" />;
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 mobile-content-padding">
        <NavigationMenu />
        <div className="flex items-center justify-center h-[calc(100vh-96px)]">
          <p className="text-gray-500 dark:text-gray-400">Документ не найден</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 mobile-content-padding">
      <NavigationMenu />
      
      <div className="container mx-auto px-4 py-6">
        {/* Заголовок и действия */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate('/knowledge')}
              variant="secondary"
              className="flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Назад
            </Button>
            
            <EditableTitle
              title={document.name}
              onUpdate={handleTitleUpdate}
              className="text-2xl font-bold text-gray-900 dark:text-white"
            />
          </div>

          <div className="flex items-center space-x-3">
            <Button
              onClick={addNewBoard}
              variant="primary"
              isIcon
              title="Добавить доску"
            >
              <Plus className="w-6 h-6" />
            </Button>
            
            <Button
              onClick={saveDocument}
              disabled={!hasChanges || isSaving}
              variant={hasChanges && !isSaving ? "success" : "secondary"}
              className="flex items-center"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </div>
        </div>

        {/* Индикатор изменений */}
        {hasChanges && (
          <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-yellow-800 dark:text-yellow-200 text-sm">
              У вас есть несохраненные изменения
            </p>
          </div>
        )}

        {/* Канбан доски */}
        <div className="space-y-8">
          {kanbanData.boards.map((board, index) => (
            <motion.div
              key={board.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Заголовок доски */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <EditableTitle
                  title={board.title}
                  onUpdate={(newTitle) => updateBoard(board.id, { title: newTitle })}
                  className="text-lg font-semibold text-gray-900 dark:text-white"
                />
                
                {kanbanData.boards.length > 1 && (
                  <Button
                    onClick={() => deleteBoard(board.id)}
                    variant="danger"
                    size="sm"
                    className="flex items-center"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Удалить
                  </Button>
                )}
              </div>

              {/* Канбан доска */}
              <div className="p-4">
                <KanbanBoardStandalone
                  boardState={convertToBoardState(board)}
                  boardId={board.id}
                  itemId={itemId || ''}
                  onBoardChange={(boardId: string, newState: BoardState) => handleBoardUpdate(boardId, newState)}
                  onBoardDelete={kanbanData.boards.length > 1 ? deleteBoard : undefined}
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Пустое состояние */}
        {kanbanData.boards.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Нет досок для отображения
            </p>
            <Button
              onClick={addNewBoard}
              variant="primary"
              className="flex items-center mx-auto"
              title="Создать первую доску"
            >
              <Plus className="w-6 h-6 mr-2" />
              Создать первую доску
            </Button>
          </div>
        )}
      </div>
    </div>
  );
} 